/**
 * Auth Store - Zustand store pentru autentificare
 */

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import axios from "axios";
import i18n from "../i18n";
import { api } from "../lib/api";
import { authPersistStorage } from "../lib/authPersistStorage";
import {
  invalidateInFlightRefresh,
  refreshTokens,
  registerAuthClearedSink,
  registerAuthTokenSink,
  registerBackendUnavailableSink,
  registerCurrentUserIdResolver,
  revokeRefreshToken,
  subscribeAuthWaking,
} from "../lib/authRefresh";
import { authGeneration, resetAuthSession } from "../lib/authSession";
import {
  AUTH_STORAGE_KEY,
  persistAuthTokens,
  readPersistedAuthTokens,
} from "../lib/authStorage";
import { AuthLoginError, toAuthLoginError } from "../lib/authLoginError";
import { shouldRefreshAccessToken } from "../lib/jwtAccessToken";
import { userIdFromAccessToken } from "../lib/jwtUserId";

export interface User {
  id: number;
  username?: string | null;
  email: string;
  first_name: string;
  last_name: string;
  role: "admin" | "family" | "viewer";
  avatar_url?: string;
  permissions: string[];
  preferred_language?: string;
  phone?: string | null;
  email_verified?: boolean;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isWakingUp: boolean;
  isBackendUnavailable: boolean;

  // Actions
  hydrateAuth: () => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  retryAuth: () => Promise<void>;
  syncUserFromApi: (options?: { retriedAfter401?: boolean }) => Promise<void>;
  setUser: (user: User) => void;
  hasPermission: (permission: string) => boolean;
  getDefaultLanguage: () => string;
}

function isTransientUserFetchError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return true;
  }
  if (!error.response) {
    return true;
  }
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504 || status >= 500;
}

function resolveStoredUserId(
  accessToken: string | null,
  user: User | null,
): number | null {
  if (user?.id) {
    return user.id;
  }
  return userIdFromAccessToken(accessToken);
}

let hydrateInFlight: Promise<void> | null = null;

function clearLocalAuthWithoutRevoke(): void {
  resetAuthSession();
  useAuthStore.setState({
    user: null,
    accessToken: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: false,
    isWakingUp: false,
    isBackendUnavailable: false,
  });
  persistAuthTokens(null, null);
  delete api.defaults.headers.common["Authorization"];
}

/** Resolve effective language and sync both i18n and localStorage */
function syncLanguage(user: { preferred_language?: string; role?: string }) {
  const language =
    user.preferred_language || (user.role === "admin" ? "en" : "ro");
  i18n.changeLanguage(language);
  try {
    const saved = JSON.parse(localStorage.getItem("fh-appearance") || "{}");
    saved.language = language;
    localStorage.setItem("fh-appearance", JSON.stringify(saved));
  } catch { /* ignore */ }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,
      isWakingUp: false,
      isBackendUnavailable: false,

      hydrateAuth: async () => {
        if (hydrateInFlight) {
          await hydrateInFlight;
          return;
        }

        hydrateInFlight = (async () => {
          resetAuthSession();
          const { accessToken, refreshToken } = readPersistedAuthTokens();

          if (!refreshToken) {
            get().logout();
            return;
          }

          set({
            user: null,
            accessToken,
            refreshToken,
            isAuthenticated: false,
            isLoading: true,
            isBackendUnavailable: false,
          });

          if (accessToken) {
            api.defaults.headers.common["Authorization"] =
              `Bearer ${accessToken}`;
          } else {
            delete api.defaults.headers.common["Authorization"];
          }

          if (!accessToken || shouldRefreshAccessToken(accessToken)) {
            await get().refreshAuth();
            return;
          }

          await get().syncUserFromApi();
        })();

        try {
          await hydrateInFlight;
        } finally {
          hydrateInFlight = null;
        }
      },

      login: async (username: string, password: string) => {
        const {
          refreshToken: previousRefresh,
          accessToken: previousAccess,
          isBackendUnavailable,
          isAuthenticated,
        } = get();
        const skipRevoke = isBackendUnavailable && previousRefresh;
        if (!skipRevoke && previousRefresh && isAuthenticated) {
          void revokeRefreshToken(previousRefresh, previousAccess);
        }
        invalidateInFlightRefresh();
        clearLocalAuthWithoutRevoke();
        const generation = authGeneration();
        try {
          const response = await api.post(
            "auth/login/",
            {
              email: username,
              password,
            },
            { skipGlobalErrorToast: true },
          );

          const { access, refresh, user } = response.data;
          if (generation !== authGeneration()) {
            throw new AuthLoginError(new axios.CanceledError("Login aborted"));
          }

          set({
            accessToken: access,
            refreshToken: refresh,
            user,
            isAuthenticated: true,
            isLoading: false,
            isBackendUnavailable: false,
          });
          persistAuthTokens(access, refresh);

          // Set token in API headers
          api.defaults.headers.common["Authorization"] = `Bearer ${access}`;

          syncLanguage(user);
        } catch (error) {
          set({ isLoading: false });
          throw toAuthLoginError(error);
        }
      },

      logout: () => {
        const { refreshToken, accessToken } = get();
        void revokeRefreshToken(refreshToken, accessToken);
        invalidateInFlightRefresh();
        resetAuthSession();
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          isWakingUp: false,
          isBackendUnavailable: false,
        });
        persistAuthTokens(null, null);

        // Remove token from API headers
        delete api.defaults.headers.common["Authorization"];
      },

      retryAuth: async () => {
        const { refreshToken } = get();
        if (!refreshToken) {
          get().logout();
          return;
        }
        set({ isBackendUnavailable: false, isLoading: true });
        await get().refreshAuth();
      },

      syncUserFromApi: async (options?: { retriedAfter401?: boolean }) => {
        const generation = authGeneration();
        try {
          const userResponse = await api.get("users/me/");
          const user = userResponse.data;
          if (generation !== authGeneration()) return;
          if (get().user && get().user?.id !== user.id) resetAuthSession();
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            isBackendUnavailable: false,
          });
          syncLanguage(user);
        } catch (error) {
          if (generation !== authGeneration()) return;
          if (
            axios.isAxiosError(error) &&
            error.response?.status === 401 &&
            !options?.retriedAfter401
          ) {
            const refreshResult = await refreshTokens();
            if (generation !== authGeneration()) return;
            if (refreshResult.ok) {
              api.defaults.headers.common["Authorization"] =
                `Bearer ${refreshResult.access}`;
              await get().syncUserFromApi({ retriedAfter401: true });
              return;
            }
            if (refreshResult.fatal) {
              get().logout();
              return;
            }
            if (refreshResult.accountSwitch) {
              set({ isLoading: false });
              return;
            }
            const tokens = readPersistedAuthTokens();
            set({
              isBackendUnavailable: true,
              isLoading: false,
              isAuthenticated: true,
              accessToken: tokens.accessToken ?? get().accessToken,
              refreshToken: tokens.refreshToken ?? get().refreshToken,
            });
            return;
          }
          if (isTransientUserFetchError(error)) {
            set({
              isBackendUnavailable: true,
              isLoading: false,
              isAuthenticated: true,
            });
          } else {
            get().logout();
          }
        }
      },

      refreshAuth: async () => {
        const generation = authGeneration();
        const { refreshToken } = get();

        if (!refreshToken) {
          set({ isLoading: false });
          return;
        }

        const result = await refreshTokens();
        if (generation !== authGeneration()) return;

        if (!result.ok) {
          if (result.fatal) {
            get().logout();
          } else if (result.accountSwitch) {
            set({ isLoading: false, isWakingUp: false });
          } else {
            const tokens = readPersistedAuthTokens();
            set({
              isBackendUnavailable: true,
              isLoading: false,
              isWakingUp: false,
              isAuthenticated: true,
              accessToken: tokens.accessToken ?? get().accessToken,
              refreshToken: tokens.refreshToken ?? get().refreshToken,
            });
          }
          return;
        }

        set({
          accessToken: result.access,
          refreshToken: result.refresh,
          isAuthenticated: true,
          isLoading: true,
          isBackendUnavailable: false,
        });

        api.defaults.headers.common["Authorization"] = `Bearer ${result.access}`;

        await get().syncUserFromApi();
      },

      setUser: (user: User) => {
        if (get().user?.id !== user.id) resetAuthSession();
        set({ user });
        syncLanguage(user);
      },

      hasPermission: (permission: string) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === "admin" || user.role === "family") return true;
        return user.permissions.includes(permission);
      },

      getDefaultLanguage: () => {
        const { user } = get();
        // Default to Romanian for unauthenticated users
        // Admin users get English, others get Romanian
        if (!user) return "ro";
        return user.role === "admin" ? "en" : "ro";
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => authPersistStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.accessToken) {
          api.defaults.headers.common["Authorization"] =
            `Bearer ${state.accessToken}`;
        }
      },
    },
  ),
);

registerAuthTokenSink((access, refresh) => {
  useAuthStore.setState({
    accessToken: access,
    refreshToken: refresh,
    isAuthenticated: true,
  });
  api.defaults.headers.common["Authorization"] = `Bearer ${access}`;
});

registerCurrentUserIdResolver(() => {
  const { user, accessToken } = useAuthStore.getState();
  return resolveStoredUserId(accessToken, user);
});

registerAuthClearedSink(() => {
  useAuthStore.setState({
    accessToken: null,
    refreshToken: null,
    user: null,
    isAuthenticated: false,
    isLoading: false,
    isWakingUp: false,
    isBackendUnavailable: false,
  });
});

registerBackendUnavailableSink((unavailable) => {
  if (!unavailable) return;
  const { accessToken, refreshToken } = readPersistedAuthTokens();
  useAuthStore.setState({
    isBackendUnavailable: true,
    isLoading: false,
    isWakingUp: false,
    isAuthenticated: Boolean(accessToken && refreshToken),
    accessToken: accessToken ?? useAuthStore.getState().accessToken,
    refreshToken: refreshToken ?? useAuthStore.getState().refreshToken,
  });
});

if (typeof window !== "undefined") {
  subscribeAuthWaking((waking) => {
    useAuthStore.setState({ isWakingUp: waking });
  });

  window.addEventListener("storage", (event) => {
    if (event.key !== AUTH_STORAGE_KEY && event.key !== null) return;

    const { accessToken, refreshToken } = readPersistedAuthTokens();
    const state = useAuthStore.getState();

    if (!accessToken && !refreshToken) {
      if (state.isAuthenticated || state.accessToken) {
        state.logout();
      }
      return;
    }

    if (refreshToken && refreshToken !== state.refreshToken) {
      const incomingUserId = resolveStoredUserId(accessToken, null);
      const currentUserId = resolveStoredUserId(state.accessToken, state.user);

      if (
        incomingUserId !== null &&
        currentUserId !== null &&
        incomingUserId === currentUserId
      ) {
        useAuthStore.setState({
          accessToken,
          refreshToken,
          isAuthenticated: true,
        });
        api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
        return;
      }

      resetAuthSession();
      useAuthStore.setState({
        user: null,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        isLoading: true,
        isBackendUnavailable: false,
      });
      api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      void useAuthStore.getState().syncUserFromApi();
      return;
    }

    if (accessToken && accessToken !== state.accessToken) {
      useAuthStore.setState({
        accessToken,
        isAuthenticated: true,
      });
      api.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    }
  });
}
